import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  CheckCircle2, 
  AlertTriangle, 
  Timer,
  FileText,
  TrendingUp,
  BarChart3,
  ChevronRight,
  Edit3
} from 'lucide-react';
import logger from '@/lib/logger';

function Evolution({ user }) {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [evolutionData, setEvolutionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Edit phase dates modal
  const [editDatesOpen, setEditDatesOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [dateForm, setDateForm] = useState({
    phase_start_date: '',
    phase_completion_date: '',
    protocol_submission_date: '',
    protocol_response_date: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      fetchEvolutionData();
    }
  }, [selectedProjectId]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/evolution/projects');
      setProjects(response.data);
    } catch (error) {
      logger.error('Error fetching projects:', error);
      toast.error('Erro ao carregar projetos');
    }
  };

  const fetchEvolutionData = async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    try {
      const response = await api.get(`/evolution/project/${selectedProjectId}`);
      setEvolutionData(response.data);
    } catch (error) {
      logger.error('Error fetching evolution data:', error);
      toast.error('Erro ao carregar dados de evolução');
    } finally {
      setLoading(false);
    }
  };

  const openEditDates = (phase) => {
    setSelectedPhase(phase);
    setDateForm({
      phase_start_date: phase.phase_start_date || '',
      phase_completion_date: phase.phase_completion_date || '',
      protocol_submission_date: phase.protocol_submission_date || '',
      protocol_response_date: phase.protocol_response_date || ''
    });
    setEditDatesOpen(true);
  };

  const handleSaveDates = async (e) => {
    e.preventDefault();
    if (!selectedPhase) return;

    try {
      await api.put(`/evolution/phase/${selectedPhase.phase_id}/dates`, dateForm);
      toast.success('Datas atualizadas com sucesso!');
      setEditDatesOpen(false);
      fetchEvolutionData();
    } catch (error) {
      logger.error('Error updating dates:', error);
      toast.error('Erro ao atualizar datas');
    }
  };

  const filteredProjects = statusFilter === 'all' 
    ? projects 
    : projects.filter(p => p.status === statusFilter);

  const getExecutionStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-blue-500';
      case 'in_progress': return 'bg-blue-300';
      default: return 'bg-gray-300';
    }
  };

  const getProtocolStatusColor = (status) => {
    switch (status) {
      case 'response_received': return 'bg-green-500';
      case 'awaiting_response': return 'bg-orange-400';
      default: return 'bg-gray-200';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  // Calculate timeline bar widths
  const getTimelineBarWidth = (days, maxDays) => {
    if (!days || !maxDays) return 0;
    return Math.min((days / maxDays) * 100, 100);
  };

  const maxExecutionDays = evolutionData?.timeline 
    ? Math.max(...evolutionData.timeline.map(p => p.execution_days || 0), 1) 
    : 1;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Evolução</h1>
            <p className="text-gray-600 mt-1">Acompanhe a evolução temporal das fases dos projetos</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 border border-gray-200">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[250px]">
              <Label className="text-sm text-gray-600 mb-1 block">Selecione o Projeto</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger data-testid="project-selector">
                  <SelectValue placeholder="Escolha um projeto para visualizar" />
                </SelectTrigger>
                <SelectContent>
                  {filteredProjects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      <div className="flex items-center gap-2">
                        <span>{project.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {project.status}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm text-gray-600 mb-1 block">Filtrar por Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Não Iniciado">Não Iniciado</SelectItem>
                  <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                  <SelectItem value="Concluído">Concluído</SelectItem>
                  <SelectItem value="On Hold">Pausado - Aguarda Cliente</SelectItem>
                  <SelectItem value="On Hold - Agency">Pausado - Aguarda Análise Órgão</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-800"></div>
          </div>
        )}

        {/* No Project Selected */}
        {!selectedProjectId && !loading && (
          <Card className="p-12 border border-gray-200 text-center">
            <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Selecione um Projeto</h3>
            <p className="text-gray-500">Escolha um projeto acima para visualizar sua evolução temporal</p>
          </Card>
        )}

        {/* Evolution Data */}
        {evolutionData && !loading && (
          <>
            {/* Summary Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{evolutionData.summary.total_phases}</p>
                    <p className="text-xs text-gray-500">Total Fases</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{evolutionData.summary.completed_phases}</p>
                    <p className="text-xs text-gray-500">Concluídas</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Timer className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{evolutionData.summary.average_execution_days}</p>
                    <p className="text-xs text-gray-500">Média Dias/Fase</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <FileText className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{evolutionData.summary.average_protocol_days}</p>
                    <p className="text-xs text-gray-500">Média Dias Protocolo</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{evolutionData.summary.total_project_days || '-'}</p>
                    <p className="text-xs text-gray-500">Dias do Projeto</p>
                  </div>
                </div>
              </Card>

              {evolutionData.summary.delayed_phases > 0 && (
                <Card className="p-4 border border-red-200 bg-red-50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-200 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{evolutionData.summary.delayed_phases}</p>
                      <p className="text-xs text-red-600">Atrasadas</p>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Longest Phase Info */}
            {evolutionData.summary.longest_phase?.name && (
              <Card className="p-4 border border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">Fase mais longa:</span>
                  <span className="font-medium text-gray-900">{evolutionData.summary.longest_phase.name}</span>
                  <Badge className="bg-purple-100 text-purple-700">
                    {evolutionData.summary.longest_phase.days} dias
                  </Badge>
                </div>
              </Card>
            )}

            {/* Timeline / Gantt View */}
            <Card className="p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Timeline das Fases</h2>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-blue-500"></div>
                    <span>Execução Concluída</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-blue-300"></div>
                    <span>Em Andamento</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-green-500"></div>
                    <span>Resposta Recebida</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-orange-400"></div>
                    <span>Aguardando Resposta</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {evolutionData.timeline.map((phase, index) => (
                  <TooltipProvider key={phase.phase_id}>
                    <div 
                      className={`p-4 rounded-lg border ${phase.is_delayed ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:bg-gray-50'} transition-colors`}
                      data-testid={`phase-timeline-${phase.phase_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            phase.execution_status === 'completed' ? 'bg-blue-500' : 
                            phase.execution_status === 'in_progress' ? 'bg-blue-300' : 'bg-gray-300'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{phase.name}</h4>
                            <p className="text-xs text-gray-500">
                              {phase.completion_percentage.toFixed(0)}% concluído • Peso: {phase.weight}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {phase.is_delayed && (
                            <Badge className="bg-red-100 text-red-700 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Atrasada
                            </Badge>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openEditDates(phase)}
                            data-testid={`edit-dates-${phase.phase_id}`}
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Editar Datas
                          </Button>
                        </div>
                      </div>

                      {/* Execution Bar */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Execução
                          </span>
                          <span>
                            {phase.phase_start_date ? formatDate(phase.phase_start_date) : 'Não iniciada'} 
                            {phase.phase_start_date && ' → '}
                            {phase.phase_completion_date ? formatDate(phase.phase_completion_date) : (phase.phase_start_date ? 'Em andamento' : '')}
                          </span>
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="h-6 bg-gray-100 rounded-full overflow-hidden cursor-pointer">
                              <div 
                                className={`h-full ${getExecutionStatusColor(phase.execution_status)} rounded-full transition-all flex items-center justify-end pr-2`}
                                style={{ width: `${getTimelineBarWidth(phase.execution_days, maxExecutionDays)}%`, minWidth: phase.execution_days ? '60px' : '0' }}
                              >
                                {phase.execution_days !== null && (
                                  <span className="text-xs text-white font-medium">
                                    {phase.execution_days} dias
                                  </span>
                                )}
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-sm">
                              <p><strong>Início:</strong> {formatDate(phase.phase_start_date)}</p>
                              <p><strong>Conclusão:</strong> {phase.phase_completion_date ? formatDate(phase.phase_completion_date) : 'Em andamento'}</p>
                              <p><strong>Duração:</strong> {phase.execution_days !== null ? `${phase.execution_days} dias` : '-'}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>

                      {/* Protocol Bar */}
                      {(phase.protocol_submission_date || phase.protocol_status) && (
                        <div>
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              Protocolo
                            </span>
                            <span>
                              {formatDate(phase.protocol_submission_date)}
                              {phase.protocol_submission_date && ' → '}
                              {phase.protocol_response_date ? formatDate(phase.protocol_response_date) : 'Aguardando'}
                            </span>
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="h-6 bg-gray-100 rounded-full overflow-hidden cursor-pointer">
                                <div 
                                  className={`h-full ${getProtocolStatusColor(phase.protocol_status)} rounded-full transition-all flex items-center justify-end pr-2`}
                                  style={{ width: `${getTimelineBarWidth(phase.post_protocol_days, maxExecutionDays)}%`, minWidth: phase.post_protocol_days ? '80px' : '0' }}
                                >
                                  {phase.post_protocol_days !== null && (
                                    <span className="text-xs text-white font-medium">
                                      {phase.post_protocol_days} dias {phase.protocol_status === 'awaiting_response' ? '(aguardando)' : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-sm">
                                <p><strong>Submissão:</strong> {formatDate(phase.protocol_submission_date)}</p>
                                <p><strong>Resposta:</strong> {phase.protocol_response_date ? formatDate(phase.protocol_response_date) : 'Aguardando resposta'}</p>
                                <p><strong>Duração:</strong> {phase.post_protocol_days !== null ? `${phase.post_protocol_days} dias` : '-'}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      )}

                      {/* No dates configured */}
                      {!phase.phase_start_date && !phase.protocol_submission_date && (
                        <p className="text-sm text-gray-400 italic">Nenhuma data configurada para esta fase</p>
                      )}
                    </div>
                  </TooltipProvider>
                ))}
              </div>

              {evolutionData.timeline.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">Este projeto não possui fases cadastradas</p>
                </div>
              )}
            </Card>
          </>
        )}
      </div>

      {/* Edit Dates Modal */}
      <Dialog open={editDatesOpen} onOpenChange={setEditDatesOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Datas da Fase</DialogTitle>
          </DialogHeader>
          {selectedPhase && (
            <form onSubmit={handleSaveDates} className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="font-medium text-gray-900">{selectedPhase.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phase_start_date">Data de Início</Label>
                  <Input
                    id="phase_start_date"
                    type="date"
                    value={dateForm.phase_start_date}
                    onChange={(e) => setDateForm({...dateForm, phase_start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="phase_completion_date">Data de Conclusão</Label>
                  <Input
                    id="phase_completion_date"
                    type="date"
                    value={dateForm.phase_completion_date}
                    onChange={(e) => setDateForm({...dateForm, phase_completion_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Datas de Protocolo</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="protocol_submission_date">Data de Submissão</Label>
                    <Input
                      id="protocol_submission_date"
                      type="date"
                      value={dateForm.protocol_submission_date}
                      onChange={(e) => setDateForm({...dateForm, protocol_submission_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="protocol_response_date">Data da Resposta</Label>
                    <Input
                      id="protocol_response_date"
                      type="date"
                      value={dateForm.protocol_response_date}
                      onChange={(e) => setDateForm({...dateForm, protocol_response_date: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDatesOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                  Salvar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default Evolution;
